import { type HTMLAttributes, forwardRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SectionHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Section title displayed on the left (e.g. "Live", "Topic"). */
  title: string;
  /** Callback fired when the "See All" link is clicked. */
  onSeeAll?: () => void;
}

/**
 * Section divider with a bold title and an optional "See All" action.
 *
 * Used between major content groups on the lobby page (Live, Topic,
 * Sports, etc.).
 *
 * @example
 * ```tsx
 * <SectionHeader title="Live" onSeeAll={() => navigate('/live')} />
 * <SectionHeader title="Sports" />
 * ```
 */
const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(({ className, title, onSeeAll, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center justify-between w-full', className)} {...props}>
    <h2 className="text-[18px] font-semibold tracking-[-0.36px] text-ink capitalize m-0 leading-normal">{title}</h2>
    {onSeeAll && (
      <button
        onClick={onSeeAll}
        className="flex items-center gap-0.5 bg-transparent border-none cursor-pointer font-sans text-[18px] tracking-[-0.36px] font-normal text-ink-muted capitalize p-0"
      >
        <span>See All</span>
        <ChevronRight className="size-4" />
      </button>
    )}
  </div>
));
SectionHeader.displayName = 'SectionHeader';

export { SectionHeader };
